import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Container, Stack } from 'component-library';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WidgetSlot, WidgetType } from '@/api/rest/resources/types/widget';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import {
  useActiveWidgetSlotConfiguration,
  useWidgetConfigurationByOrganizationId,
} from '@/hooks/useWidgetConfigurationByOrganizationId';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';
import { isNullish } from '@/utils/booleans';

import { CustomizableWidgetTemplateLayoutProps } from './constants';
import { useEditWidgetConfiguration } from './hooks/useEditWidgetConfiguration';

const getFormattedWidgetName = (name: string) => {
  return name
    .replace(/^(default_template_|default_)/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const SortableWidgetItem = ({
  widget,
  isVisible,
  onToggle,
}: {
  widget: WidgetSlot;
  isVisible: boolean;
  onToggle: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.name });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Stack className='flex flex-row items-center justify-between gap-4 rounded-2xl border border-divider bg-white p-4 hover:bg-neutral-black-4'>
        {isVisible && (
          <Button
            variant='text'
            className='shrink-0 cursor-grab touch-none text-text-secondary active:cursor-grabbing w-fit px-0 py-0 rounded-none hover:bg-neutral-black-4'
            {...attributes}
            {...listeners}
          >
            <Icon icon='ri-draggable' className='text-xl' />
          </Button>
        )}
        <Stack className='flex flex-row min-w-0 flex-1 items-center gap-3'>
          <Icon icon='ri-layout-4-line' className='text-xl text-text-secondary' />
          <span className='truncate typography-body1 text-text-primary'>{getFormattedWidgetName(widget.name)}</span>
        </Stack>
        <Icon
          icon={isVisible ? 'ri-subtract-line' : 'ri-add-line'}
          onClick={onToggle}
          className='shrink-0 flex h-8 w-8 items-center justify-center rounded-full text-primary-100 hover:bg-primary-12'
        />
      </Stack>
    </div>
  );
};

export const CustomizableWidgetTemplateLayout = ({
  widgetName,
  widgetType,
  overrideLayoutConfiguration,
  onSuccess,
  onCancel,
}: CustomizableWidgetTemplateLayoutProps) => {
  const { t } = useTranslation();

  const { data: allConfigurations } = useWidgetConfigurationByOrganizationId();

  const workstreamId = useWorkstreamId();
  const finalWidgetName = workstreamId ? `${widgetName}_${workstreamId}` : (widgetName ?? '');

  const customLayoutConfiguration = useActiveWidgetSlotConfiguration(finalWidgetName, widgetType ?? WidgetType.t_page);
  const currentLayoutConfiguration = customLayoutConfiguration ?? overrideLayoutConfiguration;

  const { submit, isSubmitting } = useEditWidgetConfiguration();

  const [draftLayout, setDraftLayout] = useState<WidgetSlot[]>(currentLayoutConfiguration?.widgets ?? []);
  const visibleWidgets = draftLayout.filter((widget) => widget.visible);
  const hiddenWidgets = draftLayout.filter((widget) => !widget.visible);

  const handleWidgetVisibilityToggle = (name: string) =>
    setDraftLayout((prev) =>
      prev.map((widget) => (widget.name === name ? { ...widget, visible: !widget.visible } : widget)),
    );

  const handleApply = async () => {
    if (!currentLayoutConfiguration) return;

    const isExistingLayout = (allConfigurations?.configurations ?? []).some(
      (config) => config.name === finalWidgetName && config.type === currentLayoutConfiguration.type,
    );

    const updatedExistingLayout = (allConfigurations?.configurations ?? []).map((config) =>
      config.name === finalWidgetName && config.type === currentLayoutConfiguration.type
        ? { ...config, widgets: draftLayout }
        : config,
    );
    const addNewLayout = [
      ...(allConfigurations?.configurations ?? []),
      { name: finalWidgetName, type: currentLayoutConfiguration.type, widgets: draftLayout },
    ];

    // TODO: MVP-CW - optimize to only send the updated layout configuration instead of the entire widget configuration for the organization
    const finalAllConfigurations = isExistingLayout ? updatedExistingLayout : addNewLayout;

    await submit({
      hidden_facts: allConfigurations?.hidden_facts ?? [],
      configurations: finalAllConfigurations,
    });

    onSuccess?.();
  };

  const handleCancel = () => {
    setDraftLayout(currentLayoutConfiguration?.widgets ?? []);
    onCancel?.();
  };

  const listeners = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reorderInSection = (activeId: string, overId: string) => {
    const activeIdx = visibleWidgets.findIndex((widget) => widget.name === activeId);
    const overIdx = visibleWidgets.findIndex((widget) => widget.name === overId);
    if (activeIdx === -1 || overIdx === -1) return;
    const reorderedWidgets = arrayMove(visibleWidgets, activeIdx, overIdx);
    setDraftLayout((prev) => {
      const result = [...prev];
      let widgetIdx = 0;
      for (let index = 0; index < result.length; index += 1) {
        if (!isNullish(result?.[`${index}`]) && result?.[`${index}`]?.visible === true) {
          result[`${index}`] = reorderedWidgets[`${widgetIdx}`] as WidgetSlot;
          widgetIdx += 1;
        }
      }
      return result;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (isNullish(over) || active.id === over.id) return;
    reorderInSection(active.id as string, over.id as string);
  };

  return (
    <Container gutterWidth={{ sm: 4 }} className='flex flex-col justify-start items-start gap-8 my-8 px-16'>
      <Stack className='flex flex-row w-full justify-between items-center gap-4'>
        <Stack className='self-start'>
          <span className='typography-h2 text-text-primary'>{t('shared.customizeLayout.title')}</span>
          <span className='typography-body1 text-text-secondary'>{t('shared.customizeLayout.helper')}</span>
        </Stack>
        <Stack className='self-end'>
          <Stack className='flex flex-row w-fit gap-4'>
            <Button variant='outline' onClick={handleCancel} disabled={isSubmitting}>
              {t('global.ui.buttons.cancel')}
            </Button>
            <Button onClick={handleApply} disabled={!currentLayoutConfiguration || isSubmitting}>
              {t('global.ui.buttons.apply')}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Stack className='gap-2 w-full'>
        <span className='typography-h4 text-text-secondary'>{t('shared.customizeLayout.sections.visibleWidgets')}</span>
        <span className='typography-overline text-text-secondary'>
          {t('shared.customizeLayout.widgetsCount', { count: visibleWidgets.length })}
        </span>
      </Stack>

      <DndContext sensors={listeners} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleWidgets.map((widget) => widget.name)} strategy={verticalListSortingStrategy}>
          <Stack className='grid grid-cols-1 gap-3 lg:grid-cols-2 w-full'>
            {visibleWidgets.map((widget) => (
              <SortableWidgetItem
                key={widget.name}
                widget={widget}
                isVisible={true}
                onToggle={() => handleWidgetVisibilityToggle(widget.name)}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      <Stack className='gap-2 w-full'>
        <span className='typography-h4 text-text-secondary'>{t('shared.customizeLayout.sections.hiddenWidgets')}</span>
        <span className='typography-overline text-text-secondary'>
          {t('shared.customizeLayout.widgetsCount', { count: hiddenWidgets.length })}
        </span>
      </Stack>

      <Stack className='grid grid-cols-1 gap-3 lg:grid-cols-2 w-full'>
        {hiddenWidgets.map((widget) => (
          <SortableWidgetItem
            key={widget.name}
            widget={widget}
            isVisible={false}
            onToggle={() => handleWidgetVisibilityToggle(widget.name)}
          />
        ))}
      </Stack>
    </Container>
  );
};
